'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { ArrowUpDownIcon } from '@terra/ui-icons';
import { Field } from '../../../components/field';
import { createSelectListCollection, Select } from '../../../components/select';
import {
  PreventionTargetField,
  type PreventionTargetDiscovery,
} from './PreventionTargetField';
import {
  PREVENTION_ROUTING_TARGETS,
  type PreventionRoutingTarget,
} from './prevention-routing.model';

type ConnectionRoutingTarget = Exclude<
  PreventionRoutingTarget,
  typeof PREVENTION_ROUTING_TARGETS.AWS_WAFV2
>;

type PreventionConnectionOption = {
  value: string;
  label: string;
};

export type PreventionConnectionRoutingFieldsProps = {
  target: ConnectionRoutingTarget;
  connectionOptions: PreventionConnectionOption[];
  connectionId: string;
  onConnectionChange: (connectionId: string) => void;
  scopeRef: string;
  onScopeRefChange: (scopeRef: string) => void;
  discovery: PreventionTargetDiscovery;
  isReadOnly?: boolean;
  onConfirm: () => void;
  confirmDisabled?: boolean;
  confirmLoading?: boolean;
  onClear: () => void;
  clearLoading?: boolean;
};

const TARGET_LABEL_KEYS = {
  [PREVENTION_ROUTING_TARGETS.CLOUDFLARE]: {
    connection: 'selectConnection',
    select: 'zoneSelectLabel',
    placeholder: 'zoneIdPlaceholder',
    discovering: 'zoneDiscovering',
    noneFound: 'zoneNoneFound',
    discoverFailed: 'zoneDiscoverFailed',
    refresh: 'zoneRefresh',
  },
  [PREVENTION_ROUTING_TARGETS.VERCEL]: {
    connection: 'selectVercelConnection',
    select: 'projectSelectLabel',
    placeholder: 'projectIdPlaceholder',
    discovering: 'projectDiscovering',
    noneFound: 'projectNoneFound',
    discoverFailed: 'projectDiscoverFailed',
    refresh: 'projectRefresh',
  },
} as const;

export function PreventionConnectionRoutingFields({
  target,
  connectionOptions,
  connectionId,
  onConnectionChange,
  scopeRef,
  onScopeRefChange,
  discovery,
  isReadOnly,
  onConfirm,
  confirmDisabled,
  confirmLoading,
  onClear,
  clearLoading,
}: PreventionConnectionRoutingFieldsProps) {
  const t = useTranslations('designSystem.applicationSettings.prevention');
  const keys = TARGET_LABEL_KEYS[target];
  const collection = useMemo(
    () => createSelectListCollection({ items: connectionOptions }),
    [connectionOptions],
  );
  const labels = useMemo(
    () => ({
      select: t(keys.select),
      placeholder: t(keys.placeholder),
      discovering: t(keys.discovering),
      noneFound: t(keys.noneFound),
      discoverFailed: t(keys.discoverFailed),
      refresh: t(keys.refresh),
      confirm: t('save'),
      clear: t('clear'),
    }),
    [keys, t],
  );

  return (
    <>
      <Field.Root>
        <Field.Label>{t(keys.connection)}</Field.Label>
        <Select.Root
          collection={collection}
          value={connectionId ? [connectionId] : []}
          onValueChange={({ value }) => onConnectionChange(value[0] ?? '')}
          positioning={{ sameWidth: true }}
          size="sm"
        >
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText placeholder={t(keys.connection)} />
              <ArrowUpDownIcon size={14} />
            </Select.Trigger>
          </Select.Control>
          <Select.Positioner>
            <Select.Content>
              {connectionOptions.map((option) => (
                <Select.Item key={option.value} item={option}>
                  <Select.ItemText>{option.label}</Select.ItemText>
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      </Field.Root>
      <PreventionTargetField
        discovery={discovery}
        discoveryKey={connectionId}
        value={scopeRef}
        onChange={onScopeRefChange}
        isReadOnly={isReadOnly}
        labels={labels}
        onConfirm={onConfirm}
        confirmDisabled={confirmDisabled}
        confirmLoading={confirmLoading}
        onClear={onClear}
        clearLoading={clearLoading}
      />
    </>
  );
}
